import * as React from 'react'
import { stripHtml } from "string-strip-html"

import EditorContainer from './editorContainer'
import Feedback from './feedback'
import QuailCelebration, { PERCH, VISIT } from './quailCelebration'
import SkillCheck from './skillCheck'

import { isErrorFeedback, isFullOptimal, isTargetOptimal } from '../../libs/optimalSignals'
import EditCaretPositioning from '../../../Shared/libs/editCaretPositioning'
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource'
import { GRAMMAR, RUNON } from '../../../../constants/evidence'
import { clampSelectionToStem, inputWouldEditStem, keystrokeWouldEditStem, placeCaretAtOffset, renderedTextLength } from '../../libs/protectedStem'
import { RICH_TEXT_TAGS_REGEX, highlightGrammar, highlightSpelling, stripEvidenceHtml, trailingConjunctionPattern } from '../../libs/stringFormatting'
import { ButtonSpinner, ENTER, READ_ALOUD_BUTTON_VARIANT, ReadAloudButton, promptToSpeechText } from '../../../Shared/index'

interface PromptStepProps {
  currentActivity: object,
  activityIsComplete: Boolean;
  className: string,
  completionButtonCallback: () => void;
  submitResponse: Function;
  completeStep: (event: any) => void;
  stepNumber: number;
  activateStep: (event: any) => void;
  reportAProblem: ({}) => void;
  prompt: any,
  submittedResponses: Array<any>,
  onGetFeedback?: () => void;
}

interface PromptStepState {
  html: string;
  numberOfSubmissions: number;
  customFeedback: string|null;
  customFeedbackKey: string|null;
  customFeedbackHeading: string|null;
  tooShortSubmissions: number;
  responseOverCharacterLimit: boolean;
  submissionTime: number;
  timeAtLastFeedbackSubmissionCheck: number;
  failedToLoadFeedback: boolean;
  skillCheckFilled: boolean;
  feedbackVisible: boolean;
  quailMode: string;
}

const RESPONSE = 'response'
const PROMPT = 'prompt'
export const DIRECTIONS = 'Use information from the text to finish the sentence:'
const APPROACHING_LIMIT_MESSAGE = 'Your response is getting close to the maximum length';
const OVER_LIMIT_MESSAGE = 'Your response is too long for our feedback bot to understand';
const WARNING_THRESHOLD = 160;
const LIMIT_THRESHOLD = 200;
const MAX_ATTEMPTS = 5;
const SLOW_FEEDBACK_CONSTRAINT = 10000;
const FEEDBACK_LOADING_LIMIT = 30000;
const MINIMUM_WORD_COUNT = 3;
const TOO_SHORT_HEADING = 'Too Short';
const TOO_SHORT_FEEDBACK_KEY = 'too short';
const TOO_SHORT_FEEDBACK = `Your response needs to include at least ${MINIMUM_WORD_COUNT} words.<br/><br/>Revise your response and try again.`;

// Celebration timeline (ms).
const SKILL_CHECK_FILL_DELAY = 1500;
const FEEDBACK_REVEAL_DELAY = 2100;

export class PromptStep extends React.Component<PromptStepProps, PromptStepState> {
  private editor: any // eslint-disable-line react/sort-comp
  private interval: any // eslint-disable-line react/sort-comp
  private button: any // eslint-disable-line react/sort-comp
  private celebrationTimers: Array<ReturnType<typeof setTimeout>> // eslint-disable-line react/sort-comp
  private celebratedResponseCount: number // eslint-disable-line react/sort-comp
  private cachedStem: string // eslint-disable-line react/sort-comp
  private cachedStemLength: number // eslint-disable-line react/sort-comp
  private selectingWithMouse: boolean // eslint-disable-line react/sort-comp
  private quailVisitFired: boolean // eslint-disable-line react/sort-comp
  private quailPerchFired: boolean // eslint-disable-line react/sort-comp


  constructor(props: PromptStepProps) {
    super(props)

    const { submittedResponses, } = this.props

    // Only responses submitted this session (beyond the mount count) replay the celebration.
    this.celebratedResponseCount = submittedResponses.length
    this.celebrationTimers = []
    // Each quail mode fires once per prompt, and never replays for work done before this mount.
    this.quailVisitFired = submittedResponses.some(isTargetOptimal)
    this.quailPerchFired = submittedResponses.some(isFullOptimal)

    this.state = {
      html: this.formattedPrompt(submittedResponses),
      numberOfSubmissions: submittedResponses.length,
      customFeedback: null,
      customFeedbackKey: null,
      customFeedbackHeading: null,
      tooShortSubmissions: 0,
      responseOverCharacterLimit: false,
      submissionTime: 0,
      failedToLoadFeedback: false,
      timeAtLastFeedbackSubmissionCheck: 0,
      skillCheckFilled: submittedResponses.some(isTargetOptimal),
      feedbackVisible: submittedResponses.length > 0,
      quailMode: null,
    };

    this.editor = React.createRef()
    this.button = React.createRef()
    this.interval = null;
  }

  componentDidMount() {
    document.addEventListener('selectionchange', this.handleSelectionChange)
    document.addEventListener('mouseup', this.handleMouseUp)
    if (this.editor) {
      this.editor.addEventListener('beforeinput', this.handleBeforeInput)
      this.editor.addEventListener('mousedown', this.handleMouseDown)
    }
  }

  componentDidUpdate() {
    const { submittedResponses } = this.props;
    const { numberOfSubmissions, submissionTime, timeAtLastFeedbackSubmissionCheck, failedToLoadFeedback } = this.state;
    const timeLapsed = Math.abs(timeAtLastFeedbackSubmissionCheck - submissionTime)
    const awaitingFeedback = (numberOfSubmissions !== submittedResponses.length) && !failedToLoadFeedback

    if(submissionTime && numberOfSubmissions === submittedResponses.length) {
      // The newly-submitted feedback has arrived -- only now clear any custom feedback (e.g. a
      // Too Short message shown before this submission) so it doesn't briefly revert to the
      // previously-submitted feedback while the new one is loading.
      this.setState({ submissionTime: 0, timeAtLastFeedbackSubmissionCheck: 0, customFeedback: null, customFeedbackKey: null, customFeedbackHeading: null });
      clearInterval(this.interval);
    }
    if(timeAtLastFeedbackSubmissionCheck && timeLapsed >= FEEDBACK_LOADING_LIMIT && !failedToLoadFeedback) {
      const feedbackFailedToLoadText = 'Sorry, our feedback did not load properly. Please try again or refresh the page.'
      this.setState({ failedToLoadFeedback: true, customFeedback: feedbackFailedToLoadText, customFeedbackKey: 'feedback failed' });
    }
    if(awaitingFeedback) {
      this.button.current.focus()
    }

    if (submittedResponses.length > this.celebratedResponseCount) {
      this.celebratedResponseCount = submittedResponses.length
      this.runCelebrationTimeline()
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    this.clearCelebrationTimers();
    document.removeEventListener('selectionchange', this.handleSelectionChange)
    document.removeEventListener('mouseup', this.handleMouseUp)
    if (this.editor) {
      this.editor.removeEventListener('beforeinput', this.handleBeforeInput)
      this.editor.removeEventListener('mousedown', this.handleMouseDown)
    }
  }

  clearCelebrationTimers = () => {
    this.celebrationTimers.forEach(clearTimeout)
    this.celebrationTimers = []
  }

  runCelebrationTimeline = () => {
    const { submittedResponses, } = this.props
    const lastResponse = this.lastSubmittedResponse()
    const targetMetBefore = submittedResponses.slice(0, -1).some(isTargetOptimal)
    const reachedTarget = isTargetOptimal(lastResponse)
    const staged = reachedTarget && !targetMetBefore
    const quailMode = this.quailModeFor(lastResponse, staged)

    this.clearCelebrationTimers()

    if (staged) {
      this.setState({ skillCheckFilled: false, feedbackVisible: false, quailMode })
      this.celebrationTimers.push(setTimeout(() => this.setState({ skillCheckFilled: true }), SKILL_CHECK_FILL_DELAY))
      this.celebrationTimers.push(setTimeout(() => this.setState({ feedbackVisible: true }), FEEDBACK_REVEAL_DELAY))
    } else if (reachedTarget) {
      this.setState({ skillCheckFilled: true, feedbackVisible: true, quailMode })
    } else {
      this.setState({ skillCheckFilled: targetMetBefore, feedbackVisible: true, quailMode })
    }
  }

  // A full optimal response earns a perch; hitting the target skill alone earns a passing visit.
  quailModeFor = (lastResponse, staged: boolean) => {
    if (isErrorFeedback(lastResponse)) { return null }

    if (isFullOptimal(lastResponse) && !this.quailPerchFired) {
      this.quailPerchFired = true
      return PERCH
    }

    if (staged && !this.quailVisitFired) {
      this.quailVisitFired = true
      return VISIT
    }

    return null
  }

  handleQuailDone = () => this.setState({ quailMode: null })

  submittedFinalResponse = () => {
    const { submittedResponses, prompt, } = this.props
    return submittedResponses.length === prompt.max_attempts || isFullOptimal(this.lastSubmittedResponse())
  }

  lastSubmittedResponse = () => {
    const { submittedResponses, } = this.props
    return submittedResponses.length ? submittedResponses[submittedResponses.length - 1] : {}
  }

  unsubmittableResponses = () => {
    const { submittedResponses, prompt } = this.props
    const { text } = prompt;
    return submittedResponses.map(r => r.entry).concat(stripEvidenceHtml(text))
  }

  formattedPrompt = (submittedResponses?: Array<string>) => {
    if (submittedResponses && submittedResponses.length) {
      const lastSubmission = submittedResponses[submittedResponses.length - 1]
      const formattedText = this.formatHtmlForEditorContainer(lastSubmission.entry, true)
      return formattedText.htmlWithBolding
    }
    return this.formattedStem()
  }

  formattedStem = () => {
    return `<p>${this.richTextFormattedStem()}</p>`
  }

  allButLastWord = (str: string) => str.substring(0, str.lastIndexOf(' '))

  lastWord = (str: string) => str.substring(str.lastIndexOf(' ') + 1)

  textWithoutStem = (text: string) => {
    const regex = this.promptAsRegex()
    return text.replace(regex, '')
  }

  highlightsAddedPrompt = (str: string) => {
    const { prompt, submittedResponses, } = this.props
    const lastSubmittedResponse = this.lastSubmittedResponse()

    if (!lastSubmittedResponse || !lastSubmittedResponse.highlight || !lastSubmittedResponse.entry || submittedResponses.length === prompt.max_attempts) { return str }

    const thereArePromptHighlights = lastSubmittedResponse.highlight.filter(hl => hl.type === PROMPT).length
    if (!thereArePromptHighlights) {
      return str
    }

    let wordsToFormat = lastSubmittedResponse.highlight.filter(hl => hl.type === PROMPT).map(hl => stripEvidenceHtml(hl.text))
    wordsToFormat = wordsToFormat.length === 1 ? wordsToFormat[0] : wordsToFormat
    return highlightSpelling(str, wordsToFormat)
  }

  formatStudentResponse = (str: string, promptLength: number) => {
    const { prompt, submittedResponses, } = this.props
    const lastSubmittedResponse = this.lastSubmittedResponse()

    if (!lastSubmittedResponse || !lastSubmittedResponse.highlight || !lastSubmittedResponse.entry || submittedResponses.length === prompt.max_attempts) { return str }

    const thereAreResponseHighlights = lastSubmittedResponse.highlight.filter(hl => hl.type === RESPONSE && hl.text.length).length

    if (!thereAreResponseHighlights) { return str }

    const filteredHighlights = lastSubmittedResponse.highlight.filter(hl => hl.type === RESPONSE)

    let wordsToFormat = filteredHighlights.map(hl => stripEvidenceHtml(hl.text))

    wordsToFormat = wordsToFormat.length === 1 ? wordsToFormat[0] : wordsToFormat

    if (lastSubmittedResponse.feedback_type === 'plagiarism') {
      return this.formatPlagiarismHighlight(str, wordsToFormat)
    } else if ([GRAMMAR, RUNON].includes(lastSubmittedResponse.feedback_type)) {
      return highlightGrammar(str, filteredHighlights, promptLength)
    } else {
      return highlightSpelling(str, wordsToFormat)
    }
  }

  formatPlagiarismHighlight = (str: string, wordsToFormat: string) => {
    let boldedString = `<b>${wordsToFormat}</b>`
    return str.replace(wordsToFormat, boldedString)
  }

  htmlStrippedPrompt = (escapeRegexCharacters=false) => {
    const strippedPrompt = this.formattedStem().replace(RICH_TEXT_TAGS_REGEX, '')
    if (escapeRegexCharacters) {
      return strippedPrompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }
    return strippedPrompt
  }

  editorContainerId = () => {
    const { prompt, } = this.props
    return `prompt-editor-${prompt.id}`
  }

  richTextFormattedStem = () => {
    const { prompt, } = this.props
    const { text, conjunction } = prompt
    const match = text.match(new RegExp(trailingConjunctionPattern(conjunction)))
    if (match && match.index !== undefined) {
      const before = text.slice(0, match.index)
      const closingTags = match[2] || ''
      return `${before}<u>${conjunction}</u>${closingTags}&nbsp;`
    }
    // Fallback: use plain text
    const plain = stripEvidenceHtml(text)
    return `${this.allButLastWord(plain)} <u>${this.lastWord(plain)}</u>&nbsp;`
  }

  promptAsRegex = () => new RegExp(`^${this.htmlStrippedPrompt(true)}`)

  onTextChange = (e) => {
    const { value } = e.target
    const text = value.replace(RICH_TEXT_TAGS_REGEX, '')
    const regex = this.promptAsRegex()
    const caretPosition = EditCaretPositioning.saveSelection(this.editor)
    if (text.match(regex)) {
      // when a user deletes the last period, a &nbsp; gets appended as the end of the string so we need to remove this
      const lastSpaceCorrectedValue = value.replace(/&nbsp;<\/p>/, ' </p>')
      this.setState({ html: lastSpaceCorrectedValue, }, () => EditCaretPositioning.restoreSelection(this.editor, caretPosition))
      // if the student has deleted everything, we want to remove everything but the prompt stem
    } else if (!text.length) {
      this.resetText()
    } else {
      const htmlStrippedPrompt = this.htmlStrippedPrompt()
      const splitSubmission = text.split('&nbsp;')

      // handles case where change is only in the formatted prompt part
      if (splitSubmission.length > 1) {
        const newValue = `${htmlStrippedPrompt}${splitSubmission[splitSubmission.length - 1]}`
        this.setState({ html: newValue}, () => {
          this.editor.innerHTML = newValue
          // Rewriting innerHTML wipes the selection, which is what used to strand
          // the caret at the very start of the stem.
          placeCaretAtOffset(this.editor, this.stemLength())
        })
      // student overwrote or deleted both part of their submission and the formatted prompt and the solution is much more complicated
      } else {
        const formattedPromptWordArray = htmlStrippedPrompt.split(' ')
        const textWordArray = text.replace(/&nbsp;/g, ' ').split(' ')

        // if the user has tried to edit part of the original prompt, we find the words in their submission that are different from the original prompt
        const diffIndices: number[] = []
        formattedPromptWordArray.forEach((word: string, i: number) => {
          if ((textWordArray[i] !== word)) {
            diffIndices.push(i)
          }
        })

        let newTextWordArray = textWordArray.slice(0, diffIndices[0])
        const textToAddAfterPromptText: string[] = []

        // then we add each of the words from the original prompt that they modified or removed back in
        diffIndices.forEach((originalIndex: number) => {
          const diffWordEquivalent = formattedPromptWordArray[originalIndex]
          newTextWordArray.push(diffWordEquivalent)
          const diffWordWithoutHtmlLettersArray = textWordArray[originalIndex] ? stripEvidenceHtml(textWordArray[originalIndex]).split('') : null
          if (diffWordWithoutHtmlLettersArray) {
            const diffWordEquivalentWithoutHtmlLettersArray = stripEvidenceHtml(diffWordEquivalent)
            const indexOfLettersToKeepFromDiffWord = diffWordWithoutHtmlLettersArray.findIndex((letter: string, i: number) => letter !== diffWordEquivalentWithoutHtmlLettersArray[i])
            if (indexOfLettersToKeepFromDiffWord !== -1) {
              const partOfDiffWordToKeep = diffWordWithoutHtmlLettersArray.slice(indexOfLettersToKeepFromDiffWord).join('').replace(/(&nbsp;)|(<u>)|(<\/u>)/g, '')
              // keeping track of what they'd modified it to be, so we don't lose those changes
              textToAddAfterPromptText.push(partOfDiffWordToKeep)
            }
          }
        })

        const restOfSubmission = textWordArray.slice(diffIndices[diffIndices.length - 1] + 1)

        // then we concatenate the original text, however they had changed their submission, and the rest of the submission
        newTextWordArray = newTextWordArray.concat(textToAddAfterPromptText).concat(restOfSubmission)

        const newValue = newTextWordArray.join(' ').replace(/&nbsp;\s/g, '&nbsp;')

        this.setState({ html: newValue}, () => {
          this.editor.innerHTML = newValue
          // Rewriting innerHTML wipes the selection, which is what used to strand
          // the caret at the very start of the stem.
          placeCaretAtOffset(this.editor, this.stemLength())
        })
      }
    }

    // Feedback persists until the student presses Get feedback again -- including the front-end
    // Too Short message. Other custom feedback (e.g. failed-to-load) still clears as they edit.
    const { customFeedbackHeading, } = this.state
    if (customFeedbackHeading !== TOO_SHORT_HEADING) {
      this.setState({ customFeedback: null, customFeedbackKey: null, customFeedbackHeading: null })
    }
  }

  resetText = () => {
    const html = this.formattedStem()
    this.setState({ html }, () => {
      this.editor.innerHTML = html
      this.placeCaretAtEnd()
    })
  }

  setEditorRef = (node: JSX.Element) => this.editor = node

  // The offset of the student's first editable character: everything before it
  // belongs to the sentence stem and is off limits. Memoized because
  // selectionchange fires on every caret move.
  stemLength = () => {
    const stem = this.richTextFormattedStem()

    if (stem !== this.cachedStem) {
      this.cachedStem = stem
      this.cachedStemLength = renderedTextLength(stem)
    }

    return this.cachedStemLength
  }

  handleSelectionChange = () => {
    // Clamping on every selectionchange of a drag fights the browser's own
    // anchor and makes the highlight shimmer, so a drag is clamped on release.
    if (this.selectingWithMouse) { return }
    if (!this.editor || document.activeElement !== this.editor) { return }

    clampSelectionToStem(this.editor, this.stemLength())
  }

  handleMouseDown = () => this.selectingWithMouse = true

  handleMouseUp = () => {
    if (!this.selectingWithMouse) { return }

    this.selectingWithMouse = false
    clampSelectionToStem(this.editor, this.stemLength())
  }

  handleBeforeInput = (e) => {
    if (!inputWouldEditStem(e, this.editor, this.stemLength())) { return }

    e.preventDefault()
  }

  placeCaretAtEnd = () => {
    // following code ensures tabbing into editor always puts cursor at the end of the text
    const el = this.editor
    // retrieved from https://stackoverflow.com/a/4238971
    if (typeof window.getSelection != "undefined" && typeof document.createRange != "undefined") {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else if (typeof document.body.createTextRange != "undefined") {
      const textRange = document.body.createTextRange();
      textRange.moveToElementText(el);
      textRange.collapse(false);
      textRange.select();
    }
  }

  handleClickReturnToEditor = () => {
    const scrollContainer = document.getElementsByClassName('steps-outer-container')[0] as HTMLElement | undefined;

    this.editor.focus({ preventScroll: true });

    if (!scrollContainer) return;

    const editorTop = this.editor.getBoundingClientRect().top;
    const containerTop = scrollContainer.getBoundingClientRect().top;

    const delta = editorTop - containerTop;

    scrollContainer.scrollTo({
      behavior: 'smooth',
      top: scrollContainer.scrollTop + delta,
    });
  }

  // Mirrors the backend's stem-stripping (session.ts getFeedback: entry.slice(promptText.length))
  // so the frontend word count matches what the backend would have counted.
  responseIsTooShort = (entry: string, promptText: string) => {
    const entryWithoutStem = entry.slice(promptText.length).trim()
    const wordCount = entryWithoutStem.split(/\s+/).filter(Boolean).length
    return wordCount < MINIMUM_WORD_COUNT
  }

  handleGetFeedbackClick = (e, entry: string, promptId: string, promptText: string) => {
    e.preventDefault()
    const { submitResponse, currentActivity, onGetFeedback, } = this.props

    onGetFeedback?.()

    // Too-short responses are handled entirely on the front end: show feedback without storing a
    // record or consuming an attempt (no numberOfSubmissions bump, no submitResponse call). The
    // per-submission counter makes the feedback key change each time, so the box re-mounts and
    // re-flashes when a new (still too short) response is submitted.
    if (this.responseIsTooShort(entry, promptText)) {
      this.setState(prevState => ({
        customFeedback: TOO_SHORT_FEEDBACK,
        customFeedbackKey: `${TOO_SHORT_FEEDBACK_KEY}-${prevState.tooShortSubmissions + 1}`,
        customFeedbackHeading: TOO_SHORT_HEADING,
        tooShortSubmissions: prevState.tooShortSubmissions + 1,
        failedToLoadFeedback: false
      }))
      return
    }

    // Keep any currently-shown feedback (including a Too Short message) visible while the new
    // feedback loads; it's cleared in componentDidUpdate once the new feedback arrives.
    this.setState(prevState => ({
      numberOfSubmissions: prevState.numberOfSubmissions + 1,
      submissionTime: (new Date()).getTime(),
      failedToLoadFeedback: false
    }), () => {
      const { numberOfSubmissions, } = this.state
      submitResponse(entry, promptId, promptText, numberOfSubmissions, currentActivity?.version)
      this.interval = setInterval(() => this.setState({ timeAtLastFeedbackSubmissionCheck: (new Date()).getTime() }), 1000);
    })
  }

  onStepInteraction = (e) => {
    const { key, ctrlKey, metaKey, } = e
    const { activateStep, stepNumber, } = this.props

    if (keystrokeWouldEditStem(e, this.editor, this.stemLength())) {
      e.preventDefault()
      return
    }

    if (key === "5" && ctrlKey && metaKey) {
      this.completeStep()
    } else if(key === ENTER) {
      this.handleEnterKeypress(e)
    } else {
      activateStep(stepNumber)
    }
  }

  handleEnterKeypress = (e) => {
    const { activityIsComplete, completionButtonCallback, submittedResponses, prompt } = this.props
    const { html, numberOfSubmissions, failedToLoadFeedback, responseOverCharacterLimit } = this.state
    const { id, text, max_attempts } = prompt
    const plainText = stripEvidenceHtml(text)

    const awaitingFeedback = (numberOfSubmissions !== submittedResponses.length) && !failedToLoadFeedback
    const entry = stripEvidenceHtml(html).trim()

    if (activityIsComplete) {
      completionButtonCallback()
    } else if (this.submittedFinalResponse()) {
      this.completeStep()
    } else if (this.unsubmittableResponses().includes(entry) || awaitingFeedback || responseOverCharacterLimit) {
      return
    } else {
      this.handleGetFeedbackClick(e, entry, id, plainText)
    }
  }

  completeStep = () => {
    const { completeStep, stepNumber, } = this.props

    completeStep(stepNumber)
  }

  // Grammar highlight offsets are measured against the stem the grammar API was
  // sent, which is Prompt#plain_text: it swaps each HTML tag for a space rather
  // than dropping it, so a stem with rich text is longer there than it is here.
  apiPromptLength = () => {
    const { prompt, } = this.props
    return (prompt.plain_text || stripEvidenceHtml(prompt.text)).length
  }

  formatHtmlForEditorContainer = (html: string) => {
    const { prompt, } = this.props
    const text = html.replace(RICH_TEXT_TAGS_REGEX, '').replace('&nbsp;', '')
    const plainPromptText = stripEvidenceHtml(prompt.text)
    const textWithoutStem = text.replace(plainPromptText, '').trim()
    const textForCharacterCount = stripHtml(textWithoutStem).result;
    const spaceAtEnd = text.match(/\s$/m) ? '&nbsp;' : ''
    return {
      htmlWithBolding: `<p>${this.highlightsAddedPrompt(this.richTextFormattedStem())}${this.formatStudentResponse(textWithoutStem, this.apiPromptLength())}${spaceAtEnd}</p>`,
      rawTextWithoutStem: textWithoutStem,
      textForCharacterCount
    }
  }

  getFeedbackLoadingDetails = () => {
    const { submissionTime, timeAtLastFeedbackSubmissionCheck } = this.state
    if(!submissionTime) {
      return null
    }
    const timeLapsed = Math.abs(timeAtLastFeedbackSubmissionCheck - submissionTime)
    if(timeLapsed <= SLOW_FEEDBACK_CONSTRAINT) {
      return <p>Finding feedback...</p>
    } else if(timeLapsed > SLOW_FEEDBACK_CONSTRAINT && timeLapsed <= FEEDBACK_LOADING_LIMIT) {
      return <p>Still finding feedback. Thanks for your patience!</p>
    }
    return null
  }

  renderFeedbackButton = () => {
    const { prompt, submittedResponses, completionButtonCallback, activityIsComplete} = this.props
    const { id, text, } = prompt
    const plainText = stripEvidenceHtml(text)
    const { html, numberOfSubmissions, responseOverCharacterLimit, failedToLoadFeedback } = this.state
    const entry = stripEvidenceHtml(html).trim()
    const awaitingFeedback = (numberOfSubmissions !== submittedResponses.length) && !failedToLoadFeedback
    const buttonLoadingSpinner = awaitingFeedback ? <ButtonSpinner /> : null
    let buttonCopy = 'Get feedback'
    let className = 'quill-button contained large focus-on-light'
    let onClick = (e) => this.handleGetFeedbackClick(e, entry, id, plainText)
    let disabled = false
    let ariaLabel = buttonCopy

    if (activityIsComplete) {
      onClick = completionButtonCallback
      buttonCopy = 'Done'
      ariaLabel = buttonCopy
    } else if (this.submittedFinalResponse()) {
      onClick = this.completeStep
      buttonCopy = 'Next'
      ariaLabel = buttonCopy
    } else if (this.unsubmittableResponses().includes(entry)) {
      className += ' disabled'
      disabled = true
      onClick = () => {}
      ariaLabel = submittedResponses.length ? "You already submitted this response. You must edit it before you can submit it again." : buttonCopy
    } else if (awaitingFeedback) {
      className += ' disabled'
      disabled = true
      onClick = () => {}
      buttonCopy = ''
      ariaLabel = "Waiting for feedback"
    } else if (responseOverCharacterLimit) {
      className += ' disabled'
      disabled = true
      onClick = () => {}
      ariaLabel = OVER_LIMIT_MESSAGE
    }

    return(
      <button
        aria-label={ariaLabel}
        className={className}
        disabled={disabled}
        onClick={onClick}
        ref={this.button}
        type="submit"
      >
        {buttonLoadingSpinner}
        <span>{buttonCopy}</span>
      </button>
    )
  }

  renderStudentControlFooter = () => {
    const { prompt, submittedResponses, } = this.props
    const { skillCheckFilled, } = this.state
    const attemptsCount = submittedResponses && submittedResponses.length ? submittedResponses.length : 0;
    const maxAttemptCount = prompt && prompt.max_attempts ? prompt.max_attempts : MAX_ATTEMPTS;

    return (
      <div className="student-control-footer">
        <SkillCheck met={skillCheckFilled} />
        <div className="attempts-and-feedback-button">
          <div className="attempts-container">
            <p className="number-of-attempts">{attemptsCount}</p>
            <p>{`of ${maxAttemptCount} attempts`}</p>
          </div>
          {this.renderFeedbackButton()}
        </div>
      </div>
    )
  }

  renderFeedbackSection = () => {
    const { customFeedback, customFeedbackKey, customFeedbackHeading, feedbackVisible, } = this.state
    const { submittedResponses, prompt, reportAProblem, } = this.props

    if (submittedResponses.length === 0 && !(customFeedback && customFeedbackKey)) { return }

    if (!feedbackVisible && !customFeedback) { return }

    return (
      <Feedback
        customFeedback={customFeedback}
        customFeedbackHeading={customFeedbackHeading}
        customFeedbackKey={customFeedbackKey}
        lastSubmittedResponse={this.lastSubmittedResponse()}
        prompt={prompt}
        reportAProblem={reportAProblem}
        submittedResponses={submittedResponses}
      />
    )
  }

  renderCharacterLimitWarning = (characterCount: number, characterCountClassName: string) => {
    if(characterCount < WARNING_THRESHOLD) { return }
    const message = characterCount < LIMIT_THRESHOLD ? APPROACHING_LIMIT_MESSAGE : OVER_LIMIT_MESSAGE;
    return(
      <div className="character-counter-container">
        <p className={characterCountClassName}>{message}</p>
        <p className={characterCountClassName}>{`${characterCount}/${LIMIT_THRESHOLD}`}</p>
      </div>
    )
  }

  renderEditorContainer = () => {
    const { html, responseOverCharacterLimit, quailMode } = this.state
    const { submittedResponses, prompt } = this.props
    const lastSubmittedResponse = this.lastSubmittedResponse()
    let className = 'editor'
    let disabled = false
    const outOfAttempts = submittedResponses.length === prompt.max_attempts
    if (isFullOptimal(lastSubmittedResponse)) {
      className += ' optimal disabled'
      disabled = true
    } else if (outOfAttempts) {
      className += ' suboptimal disabled'
      disabled = true
    } else if (submittedResponses.length) {
      className += ' suboptimal'
    }

    const formattedText = this.formatHtmlForEditorContainer(html)
    const { htmlWithBolding, rawTextWithoutStem, textForCharacterCount } = formattedText
    const characterCount = textForCharacterCount && textForCharacterCount.split('').length;
    let characterCountClassName;
    if(characterCount >= WARNING_THRESHOLD && characterCount < LIMIT_THRESHOLD) {
      characterCountClassName = 'approaching-limit';
    } else if (characterCount >= LIMIT_THRESHOLD) {
      characterCountClassName = 'over-limit';
    }
    if(characterCountClassName) {
      className += ` ${characterCountClassName}`;
    }
    if(characterCount >= LIMIT_THRESHOLD && !responseOverCharacterLimit) {
      this.setState({ responseOverCharacterLimit: true });
    } else if(characterCount < LIMIT_THRESHOLD && responseOverCharacterLimit) {
      this.setState({ responseOverCharacterLimit: false });
    }

    return (
      <div id={this.editorContainerId()}>
        <div className="prompt-read-aloud">
          <ReadAloudButton activeText="Stop reading" controlsId={this.editorContainerId()} idleText="Read prompt" source={clipSource(CLIP_SOURCE_TYPE.EVIDENCE_PROMPT, prompt.id)} text={promptToSpeechText(prompt.text)} variant={READ_ALOUD_BUTTON_VARIANT.LISTEN} />
        </div>
        <div className="student-control">
          {/* Wraps the editor alone, so the quail is positioned against the response box. */}
          <div className="quail-perch">
            <EditorContainer
              className={className}
              disabled={disabled}
              handleFocus={this.placeCaretAtEnd}
              handleKeyDown={this.onStepInteraction}
              handleTextChange={this.onTextChange}
              html={htmlWithBolding}
              innerRef={this.setEditorRef}
              isResettable={!!rawTextWithoutStem.length}
              promptText={prompt.text}
              resetText={this.resetText}
              stripHtml={stripEvidenceHtml}
            />
            {quailMode && <QuailCelebration key={quailMode} mode={quailMode} onDone={this.handleQuailDone} />}
          </div>
          {this.renderStudentControlFooter()}
        </div>
        {this.renderCharacterLimitWarning(characterCount, characterCountClassName)}
      </div>
    );
  }

  renderActiveContent = () => {
    return(
      <div className="active-content-container">
        {this.renderEditorContainer()}
        <div className="feedback-loading-details">{this.getFeedbackLoadingDetails()}</div>
        {this.renderFeedbackSection()}
        {!this.submittedFinalResponse() && <button className="skip-main" onClick={this.handleClickReturnToEditor} type="button">Return to editor</button>}
      </div>
    )
  }

  render() {
    const { className } = this.props

    return (
      <div className={className}>
        <div className="step-content">
          {this.renderActiveContent()}
        </div>
      </div>
    )
  }
}

export default PromptStep;
